import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import './Header.css';

function Header() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, loginWithRedirect, logout } = useAuth();

  const [scrolled, setScrolled] = useState(false);
  const [hasGameBadge, setHasGameBadge] = useState(false);
  const [badgeChecked, setBadgeChecked] = useState(false);

  const API_HOST = useMemo(() => (
    process.env.REACT_APP_USERS_API_HOST ||
    process.env.REACT_APP_API_HOST ||
    ''
  ), []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 16);
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function checkBadges() {
      if (!isAuthenticated || !token || !user?.sub || !API_HOST) {
        setHasGameBadge(false);
        setBadgeChecked(true);
        return;
      }

      try {
        setBadgeChecked(false);

        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        };

        const gamesRes = await fetch(`${API_HOST}/private/games`, { headers });
        if (!gamesRes.ok) throw new Error(`Failed to load games (${gamesRes.status})`);

        const games = await gamesRes.json();
        const candidateGames = Array.isArray(games) ? games.filter((game) => game?._id) : [];

        let foundBadge = false;

        for (const game of candidateGames) {
          const badgeRes = await fetch(
            `${API_HOST}/private/badges/exists/${encodeURIComponent(user.sub)}/${game._id}`,
            { headers }
          );

          if (!badgeRes.ok) continue;

          const data = await badgeRes.json();
          if (data?.exists) {
            foundBadge = true;
            break;
          }
        }

        if (!cancelled) {
          setHasGameBadge(foundBadge);
          setBadgeChecked(true);
        }
      } catch (err) {
        console.warn('[Header badge check failed]', err);
        if (!cancelled) {
          setHasGameBadge(false);
          setBadgeChecked(true);
        }
      }
    }

    checkBadges();

    return () => {
      cancelled = true;
    };
  }, [API_HOST, isAuthenticated, token, user?.sub]);

  const showNxlMarket = isAuthenticated && badgeChecked && hasGameBadge;

  return (
    <header className={`header ${scrolled ? 'header-scrolled' : ''}`}>
      <div className="header-content">
        <button className="header-logo-button" onClick={() => navigate('/')}>
          RKE.world
        </button>

        <nav className="header-nav" aria-label="Primary navigation">
          <NavLink to="/" end>Home</NavLink>

          <NavLink to="/nxl/schedule">NXL Schedule</NavLink>

          {showNxlMarket && (
            <NavLink to="/nxl">NXL Prediction Market</NavLink>
          )}

          {isAuthenticated ? (
            <>
              <button className="header-profile-button" onClick={() => navigate('/profile')}>
                {user?.picture ? (
                  <img src={user.picture} alt={user?.name || 'Profile'} />
                ) : (
                  <span>{String(user?.name || 'U').charAt(0)}</span>
                )}
              </button>

              <button
                className="header-logout-button"
                onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              >
                Log Out
              </button>
            </>
          ) : (
            <button className="header-login-button" onClick={loginWithRedirect}>
              Log In
            </button>
          )}
        </nav>
      </div>
    </header>
  );
}

export default Header;
